import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { HeadingWrap, Span } from '../../../Styles/CommonStyles';
import useAuth from '../../../features/hooks/useAuth';
import { useGetofficesQuery } from '../../../features/offices/officesApiSlice';
import UnivMyAssetComp from './UnivMyAssetComp';
import { useSelector } from 'react-redux';

// Table Pagination Actions Component
function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const theme = useTheme();

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Helper function to find subordinate offices
const findSubordinateOffices = (offices, officeName, result = []) => {
  if (!offices || !Array.isArray(offices) || !officeName) {
    return result;
  }
  
  const subOffices = offices.filter(office => office && office.superioroffice === officeName);
  for (const subOffice of subOffices) {
    if (subOffice && subOffice.name) {
      result.push(subOffice);
      findSubordinateOffices(offices, subOffice.name, result);
    }
  }
  return result;
};

export default function CustomPaginationActionsTable() {
  const Auth = useAuth();
  const [limit, setLimit] = useState(5);
  const [lastId, setLastId] = useState(null);
  const [filterProps, setFilterProps] = useState({  });
  
  const userCurrentOffice = useSelector((state) => state.office.off);

  const { offices } = useGetofficesQuery("Offices", {
    selectFromResult: ({ data }) => ({
      offices: data?.entities,
    }),
  });

  useEffect(() => {
    if (!Auth || !Auth.roles ) return;

    const roles = Auth.roles;
    const mainRoles = roles?.main || [];
    const userRoles = roles?.[userCurrentOffice] || [];
  
    let updatedFilterProps = {};
  
    if (mainRoles.includes("Admin") || mainRoles.includes("SuperAdmin")) {
      return;
    }
  
    if (userRoles.includes("level1approver") || userRoles.includes("level2approver") || userRoles.includes("assetadder")) {
      updatedFilterProps = { perm_cur_store: userCurrentOffice };
    } else if (userRoles?.includes("UnitHead")) {
      const subordinateOffices = offices ? findSubordinateOffices(Object.values(offices), userCurrentOffice)
        ?.map(office => office.name) : [];
      updatedFilterProps = { perm_alloc_ofc: subordinateOffices.concat([userCurrentOffice]) };
    } else if (userRoles.includes("Bo")) {
      updatedFilterProps = { perm_alloc_ofc: [userCurrentOffice] };
    } else if (mainRoles.length === 1 && mainRoles.includes("user")) {
      updatedFilterProps = { perm_alloc_user: [Auth.id] };
    }
    
    setFilterProps(prevProps => {
      return JSON.stringify(prevProps) !== JSON.stringify(updatedFilterProps) ? updatedFilterProps : prevProps;
    });
  
  }, [Auth?.roles, userCurrentOffice, offices]);

  const keyArrayDwld = [
    "category", "name", "oem_tag", "org_tag", "oem", "cur_user_name", "alloc_ofc", 
    "specification", "pur_store", "in_transit", "cur_store", "cur_loc_name", 
    "purchase_date", "installation_date", "warrantyexp_date", "unit_price", 
    "last_trans_date"
  ];

  const columnFilter = [
    "OEM/ORG Tag", "OEM/Brand", "User", "Store", "Alloc_Office", "Loc_Type",
    "Purchase Date", "Warranty Date", "Detail", "Model", "Indi_Issue"
  ];
  console.log("filterProps", filterProps);

  return (
    <>    
      <HeadingWrap>
        <Span>Assets Detail</Span>
      </HeadingWrap>
      <UnivMyAssetComp 
        keyArrayDwld={keyArrayDwld} 
        filterProps={filterProps}  
        columnFilter={columnFilter}  
        limit={limit} 
        setLimit={setLimit} 
        lastId={lastId} 
        setLastId={setLastId} 
      />
    </>
  );
}
